import React from 'react';
import { Box, Container, Typography, Link, Grid, IconButton, SxProps, Theme } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { usePrelaunch } from '../../contexts/PrelaunchContext';
import { 
  X as XIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

interface PublicFooterProps {
  sx?: SxProps<Theme>;
}

const PublicFooter: React.FC<PublicFooterProps> = ({ sx: externalSx }) => {
  const { isPrelaunch } = usePrelaunch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: <XIcon />, url: 'https://twitter.com/WaveScoopApp', label: 'X (Twitter)' },
    { icon: <LinkedInIcon />, url: 'https://linkedin.com/company/wavescoop', label: 'LinkedIn' },
    { icon: <RocketLaunchIcon />, url: 'https://www.producthunt.com/products/wavescoop', label: 'Product Hunt' },
    { icon: <InstagramIcon />, url: 'https://instagram.com/wavescoop.app', label: 'Instagram' },
  ];

  const linkStyle = {
    color: 'rgba(255, 255, 255, 0.8)',
    textDecoration: 'none',
    fontSize: '0.95rem',
    transition: 'color 0.2s ease',
    '&:hover': {
      color: 'white',
      textDecoration: 'none',
    },
  };

  const headingStyle = {
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    mb: 2,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  return (
    <Box 
      component="footer"
      sx={{
        backgroundColor: 'transparent',
        color: 'rgba(255, 255, 255, 0.8)',
        py: 6,
        mt: 'auto',
        position: 'relative',
        zIndex: 1,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        ...externalSx
      }}
    >
      <Container maxWidth="lg">
        {!isPrelaunch ? (
          <>
            {/* Main Footer Content - 4 Columns */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
              {/* Product Column */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={headingStyle}>
                  Product
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Link 
                    component="a"
                    href="/#features"
                    onClick={(e) => {
                      e.preventDefault();
                      if (location.pathname === '/') {
                        // Already on landing page, just scroll
                        const element = document.querySelector('#features');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      } else {
                        // Navigate to landing page, then scroll after navigation
                        navigate('/');
                        setTimeout(() => {
                          const element = document.querySelector('#features');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        }, 100);
                      }
                    }}
                    sx={linkStyle}
                  >
                    Features
                  </Link>
                  <Link 
                    component={RouterLink} 
                    to="/pricing" 
                    sx={linkStyle}
                  >
                    Pricing
                  </Link>
                </Box>
              </Grid>

              {/* Company Column */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={headingStyle}>
                  Company
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Link 
                    component={RouterLink} 
                    to="/support" 
                    sx={linkStyle}
                  >
                    Contact
                  </Link>
                  <Link 
                    component={RouterLink} 
                    to="/blog" 
                    sx={linkStyle}
                  >
                    News
                  </Link>
                </Box>
              </Grid>

              {/* Legal Column */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={headingStyle}>
                  Legal
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Link 
                    component={RouterLink} 
                    to="/legal/privacy-policy" 
                    sx={linkStyle}
                  >
                    Privacy Policy
                  </Link>
                  <Link 
                    component={RouterLink} 
                    to="/legal/terms-of-service" 
                    sx={linkStyle}
                  >
                    Terms of Service
                  </Link>
                </Box>
              </Grid>

              {/* Resources Column */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={headingStyle}>
                  Resources
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link 
                    component={RouterLink} 
                    to="/blog/ideas-update" 
                    onClick={() => window.scrollTo(0, 0)}
                    sx={linkStyle}
                  >
                    Idea Generation
                  </Link>
                  <Link 
                    component={RouterLink} 
                    to="/blog/post-simulation-tool" 
                    onClick={() => window.scrollTo(0, 0)}
                    sx={linkStyle}
                  >
                    Post Simulation
                  </Link>
                  <Link 
                    component={RouterLink} 
                    to="/blog/social-media-sentiment-analysis-tools" 
                    onClick={() => window.scrollTo(0, 0)}
                    sx={linkStyle}
                  >
                    Best Sentiment Analysis Tools
                  </Link>
                  <Link 
                    component={RouterLink} 
                    to="/blog/social-media-sentiment-analysis" 
                    onClick={() => window.scrollTo(0, 0)}
                    sx={linkStyle}
                  >
                    Sentiment Analysis Guide
                  </Link>
                </Box>
              </Grid>
            </Grid>

            {/* Bottom Bar */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                pt: 3,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              {/* Copyright */}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.875rem',
                }}
              >
                © {currentYear} WaveScoop. All rights reserved.
              </Typography>

              {/* Social Icons */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {socialLinks.map((social, index) => (
                  <IconButton
                    key={index}
                    component="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: 'white',
                      },
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>
            </Box>
          </>
        ) : (
          /* Prelaunch Mode - Simplified Footer */
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.875rem',
              }}
            >
              © {currentYear} WaveScoop. All rights reserved.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PublicFooter;